import type { WithdrawalPenaltyPreview } from '../../types/finance';

/**
 * Pure decision logic for the client withdrawal form.
 *
 * Lives apart from `Withdraw.tsx` for the reason every other `*.helpers.ts` in this
 * folder does: components here are covered only by Playwright, so anything with a rule
 * worth guaranteeing is extracted and unit-tested directly. The rule guaranteed here is
 * the owner's (2026-08-07): withdrawing early is allowed, but the client must have seen
 * the exact charge and explicitly accepted it — so the submit button cannot become
 * enabled while a charge is quoted and the confirmation is unticked.
 */

/** Does this quote actually charge the client anything? */
export function hasEarlyWithdrawalCharge(
  preview: WithdrawalPenaltyPreview | null,
): boolean {
  if (preview === null) return false;
  const charge = Number(preview.penalty_amount);
  return Number.isFinite(charge) && charge > 0;
}

export interface WithdrawFormState {
  readonly productId: string;
  readonly accountId: string | null;
  readonly amount: string;
  readonly availableBalance: string;
  readonly mfaCode: string;
  /** The live quote for the amount above, or null while it is unknown. */
  readonly preview: WithdrawalPenaltyPreview | null;
  /** True once the client ticks the confirmation showing the exact charge. */
  readonly chargeAccepted: boolean;
  /** True while the quote for the CURRENT amount is still being fetched. */
  readonly quoting: boolean;
  /** True when the quote for the current amount could not be obtained at all. */
  readonly quoteFailed: boolean;
}

/**
 * May this request be submitted? Every condition is a hard one — there is no
 * "warn and allow" branch, because the two that are new here are money rules:
 * a charge is never accepted implicitly, and a charge is never accepted blind
 * (an in-flight quote means the number on screen may not be the number charged).
 */
export function canSubmitWithdrawal(state: WithdrawFormState): boolean {
  const amount = Number(state.amount);
  const available = Number(state.availableBalance);
  const amountValid =
    state.amount.trim() !== ''
    && Number.isFinite(amount)
    && amount > 0
    && (!Number.isFinite(available) || amount <= available);

  if (!state.productId || !state.accountId || !amountValid) return false;
  if (state.mfaCode.trim().length < 6) return false;
  if (state.quoting) return false;
  // An unpriced withdrawal may carry a charge nobody has seen. The request itself
  // re-prices through the same path, so it would fail anyway: blocking here costs
  // nothing legitimate and keeps the charge from ever being accepted blind.
  if (state.quoteFailed) return false;
  if (hasEarlyWithdrawalCharge(state.preview)) return state.chargeAccepted;
  return true;
}

/**
 * The acceptance to send with the request: the exact quoted charge, or nothing at all
 * when no charge applies. Never sends a figure the client did not tick — an
 * un-ticked confirmation must reach the server as no acceptance, so the server's own
 * refusal is the backstop rather than something only the UI enforces.
 */
export function acknowledgedPenaltyToSend(
  state: Pick<WithdrawFormState, 'preview' | 'chargeAccepted'>,
): string | undefined {
  if (!hasEarlyWithdrawalCharge(state.preview) || !state.chargeAccepted) return undefined;
  return state.preview?.penalty_amount;
}

/**
 * What a client must know before withdrawing: capital earns by completed TERM, counted
 * from the day each contribution started. The result of every term it completed is the
 * client's; a term left before its end earns nothing, whatever days it had run; and a
 * term is followed by the next one without the client doing anything, and who receives the
 * result of a term left early (the managing series, R-8). Shown before the request is made,
 * never discovered afterwards.
 */
export const RESULT_ON_EXIT_NOTICE =
  'Your capital earns by completed term. The result of each term you complete is yours, '
  + 'and the next term starts by itself. '
  + 'If you withdraw capital before its current term is complete, it earns nothing for that term; '
  + "the result of that term belongs to the holder of the series' voting shares, a QMI company.";
